import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const PROGRAMS = ["PhD", "EdD", "DBA", "DNP", "PsyD", "DrPH", "Masters"];
const STAGES = [
  { v: "coursework", l: "Coursework" }, { v: "comps", l: "Comprehensive / General Exams" },
  { v: "topic_development", l: "Dissertation Topic Development" },
  { v: "proposal", l: "Proposal Development" }, { v: "proposal_defense", l: "Proposal Defense" },
  { v: "irb", l: "IRB / Ethics Approval" }, { v: "research", l: "Research / Data Collection" },
  { v: "analysis", l: "Data Analysis" }, { v: "dissertation", l: "Dissertation Writing" },
  { v: "final_review", l: "Final Dissertation Review" }, { v: "defense", l: "Dissertation Defense" },
  { v: "revisions", l: "Revisions" }, { v: "graduation", l: "Graduation" },
  { v: "publishing", l: "Publishing / Career Development" },
];
const STAGE_KEYS = STAGES.map((s) => s.v);
const METHODOLOGIES = ["Quantitative", "Qualitative", "Mixed Methods", "Undecided", "Other"];
const QUAL_SUB = ["Phenomenology", "Grounded Theory", "Case Study", "Ethnography", "Narrative Research", "Action Research", "Other"];
const QUANT_SUB = ["Experimental", "Quasi-Experimental", "Correlational", "Descriptive", "Survey Research", "Other"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    display_name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
    biography: "", country: "", languages_text: "",
    degree_type: "PhD", university: "", school: "", discipline: "",
    program_name: "", expected_graduation_year: "",
    research_interests_text: "", research_topic: "", dissertation_title: "",
    methodology: "Undecided", methodology_subtype: "",
    current_stage: "coursework",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const [askMarkPrevious, setAskMarkPrevious] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sub = form.methodology === "Qualitative" ? QUAL_SUB
    : form.methodology === "Quantitative" ? QUANT_SUB : null;

  const canNext = () => {
    if (step === 1) return form.display_name.trim();
    if (step === 2) return form.degree_type && form.university.trim() && form.discipline.trim();
    if (step === 3) return form.methodology;
    if (step === 4) return form.current_stage;
    return true;
  };

  const stageLabel = STAGES.find((s) => s.v === form.current_stage)?.l || form.current_stage;
  const isPastCoursework = STAGE_KEYS.indexOf(form.current_stage) > 0;

  const doSubmit = async (markPreviousComplete) => {
    setSubmitting(true);
    try {
      await api.post("/onboarding/complete", {
        display_name: form.display_name, biography: form.biography,
        country: form.country,
        languages: form.languages_text.split(",").map((s) => s.trim()).filter(Boolean),
        degree_type: form.degree_type, university: form.university,
        school: form.school, discipline: form.discipline, program_name: form.program_name,
        expected_graduation_year: form.expected_graduation_year ? Number(form.expected_graduation_year) : null,
        research_interests: form.research_interests_text.split(",").map((s) => s.trim()).filter(Boolean),
        research_topic: form.research_topic, dissertation_title: form.dissertation_title,
        methodology: form.methodology.toLowerCase().includes("undecided") ? "undecided" : form.methodology,
        methodology_subtype: form.methodology_subtype, current_stage: form.current_stage,
        mark_previous_complete: !!markPreviousComplete,
      });
      await refresh();
      toast.success("Welcome to DocCircle.");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
      setAskMarkPrevious(false);
    }
  };

  const submit = () => {
    if (isPastCoursework) {
      setAskMarkPrevious(true);
    } else {
      doSubmit(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-12" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.25em] text-primary">Onboarding · Step {step} of 4</p>
        <div className="mt-4 mb-8 flex gap-1.5">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`h-1 flex-1 rounded-full ${n <= step ? "bg-primary" : "bg-secondary"}`} />
          ))}
        </div>

        <Card className="p-6 lg:p-10">
          {step === 1 && (
            <div className="space-y-5" data-testid="onboard-step-1">
              <h1 className="font-serif text-3xl font-black tracking-tight">About you</h1>
              <p className="text-sm text-muted-foreground">A little context helps us match you with the right people.</p>
              <div><Label>Display name</Label><Input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="ob-display-name" /></div>
              <div><Label>Short biography (optional)</Label><Textarea value={form.biography} onChange={(e) => set("biography", e.target.value)} className="min-h-[80px]" data-testid="ob-bio" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Country</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. United States" data-testid="ob-country" /></div>
                <div><Label>Languages (optional)</Label><Input value={form.languages_text} onChange={(e) => set("languages_text", e.target.value)} placeholder="English, Spanish" data-testid="ob-languages" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="onboard-step-2">
              <h1 className="font-serif text-3xl font-black tracking-tight">Your doctoral program</h1>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Degree type</Label>
                  <Select value={form.degree_type} onValueChange={(v) => set("degree_type", v)}>
                    <SelectTrigger data-testid="ob-degree"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Expected graduation year (optional)</Label><Input type="number" value={form.expected_graduation_year} onChange={(e) => set("expected_graduation_year", e.target.value)} placeholder="2028" data-testid="ob-grad-year" /></div>
              </div>
              <div><Label>University / Institution</Label><Input value={form.university} onChange={(e) => set("university", e.target.value)} data-testid="ob-university" /></div>
              <div><Label>School / College (optional)</Label><Input value={form.school} onChange={(e) => set("school", e.target.value)} data-testid="ob-school" /></div>
              <div><Label>Academic discipline</Label><Input value={form.discipline} onChange={(e) => set("discipline", e.target.value)} placeholder="e.g. Educational Leadership" data-testid="ob-discipline" /></div>
              <div><Label>Program / Concentration (optional)</Label><Input value={form.program_name} onChange={(e) => set("program_name", e.target.value)} data-testid="ob-program" /></div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="onboard-step-3">
              <h1 className="font-serif text-3xl font-black tracking-tight">Your research</h1>
              <div><Label>Research interests (comma-separated)</Label><Input value={form.research_interests_text} onChange={(e) => set("research_interests_text", e.target.value)} placeholder="equity, leadership" data-testid="ob-interests" /></div>
              <div><Label>Research topic (optional)</Label><Input value={form.research_topic} onChange={(e) => set("research_topic", e.target.value)} data-testid="ob-topic" /></div>
              <div><Label>Dissertation working title (optional)</Label><Input value={form.dissertation_title} onChange={(e) => set("dissertation_title", e.target.value)} data-testid="ob-dissertation-title" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Methodology</Label>
                  <Select value={form.methodology} onValueChange={(v) => { set("methodology", v); set("methodology_subtype", ""); }}>
                    <SelectTrigger data-testid="ob-methodology"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODOLOGIES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {sub && (
                  <div><Label>Approach</Label>
                    <Select value={form.methodology_subtype} onValueChange={(v) => set("methodology_subtype", v)}>
                      <SelectTrigger data-testid="ob-methodology-subtype"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{sub.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="onboard-step-4">
              <h1 className="font-serif text-3xl font-black tracking-tight">Where are you in the journey?</h1>
              <p className="text-sm text-muted-foreground">This stays private by default. You can update it any time.</p>
              <div><Label>Current stage</Label>
                <Select value={form.current_stage} onValueChange={(v) => set("current_stage", v)}>
                  <SelectTrigger data-testid="ob-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="mt-10 flex justify-between">
            <Button variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="rounded-full" data-testid="ob-back-btn">Back</Button>
            {step < 4 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="rounded-full" data-testid="ob-next-btn">Continue</Button>
            ) : (
              <Button onClick={submit} disabled={!canNext() || submitting} className="rounded-full" data-testid="ob-finish-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Complete onboarding
              </Button>
            )}
          </div>
        </Card>

        {askMarkPrevious && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-6" data-testid="ob-mark-previous-modal">
            <Card className="max-w-md w-full p-8">
              <h2 className="font-serif text-2xl font-black tracking-tight">Mark earlier stages as completed?</h2>
              <p className="text-sm text-muted-foreground mt-3">
                You said you're currently at <span className="text-foreground font-medium">{stageLabel}</span>.
                Would you like DocCircle to mark all previous stages as completed on your private journey timeline?
              </p>
              <p className="text-xs text-muted-foreground mt-3">You can always edit any stage later from the Journey page.</p>
              <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3">
                <Button variant="outline" onClick={() => doSubmit(false)} disabled={submitting} className="rounded-full" data-testid="ob-mark-previous-no">
                  No, leave them
                </Button>
                <Button onClick={() => doSubmit(true)} disabled={submitting} className="rounded-full" data-testid="ob-mark-previous-yes">
                  Yes, mark previous complete
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
